import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import GalleryUploader from "@/components/GalleryUploader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES } from "@/lib/constants";
import { LANGUAGES, AVAILABILITY_OPTIONS, SKILLS_BY_CATEGORY, GENERIC_SKILLS, SOCIAL_PLATFORMS, detectSocialUrl } from "@/lib/profileOptions";
import { toast } from "sonner";
import { Loader2, LogOut, Instagram, Youtube, Facebook, Linkedin, Twitter, AtSign, Camera, Globe, Check, Plus, Lock, Mail, ShieldCheck, FileText, Film, Send, Trash2, Download } from "lucide-react";
import { errMsg } from "@/lib/api";

const SOCIAL_ICONS = {
  instagram: Instagram, youtube: Youtube, facebook: Facebook,
  twitter: Twitter, linkedin: Linkedin, threads: AtSign,
  snapchat: Camera, portfolio_url: Globe,
};

function ChipGroup({ options, selected, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((opt) => {
        const isSel = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            data-testid={`chip-${opt.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g,'')}`}
            onClick={() => onChange(isSel ? selected.filter(s => s !== opt) : [...selected, opt])}
            className={`inline-flex items-center gap-1.5 rounded-full text-sm h-9 px-4 border transition-colors ${isSel ? "bg-[#DC2626] border-[#DC2626] text-white" : "bg-zinc-900 border-zinc-700 text-zinc-100 hover:border-[#DC2626]/50 hover:text-white"}`}
          >
            {isSel && <Check size={13}/>}
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function AddCustom({ onAdd, placeholder }) {
  const [val, setVal] = useState("");
  return (
    <div className="mt-2 flex gap-2">
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (val.trim()) { onAdd(val.trim()); setVal(""); } } }}
        placeholder={placeholder}
        className="bg-zinc-900 border-zinc-700 h-9 text-sm"
      />
      <Button
        type="button"
        onClick={() => { if (val.trim()) { onAdd(val.trim()); setVal(""); } }}
        className="rounded-full h-9 bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 px-3"
      >
        <Plus size={14}/>
      </Button>
    </div>
  );
}

export default function EditProfile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    category: "", bio: "", city: "", state: "",
    languages: [], skills: [], availability: [],
    experience: "", education: "", awards: "",
    height_cm: "", weight_kg: "", gender: "", phone: "",
    instagram: "", youtube: "", facebook: "", twitter: "",
    linkedin: "", threads: "", snapchat: "",
    portfolio_url: "", showreel_url: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    const p = user.profile || {};
    const availArr = Array.isArray(p.availability) ? p.availability : (p.availability ? [p.availability] : []);
    setF({
      category: user.category || "",
      bio: p.bio || "", city: p.city || "", state: p.state || "",
      languages: p.languages || [],
      skills: p.skills || [],
      availability: availArr,
      experience: p.experience || "", education: p.education || "", awards: p.awards || "",
      height_cm: p.height_cm || "", weight_kg: p.weight_kg || "",
      gender: p.gender || "", phone: p.phone || "",
      instagram: p.instagram || "", youtube: p.youtube || "",
      facebook: p.facebook || "", twitter: p.twitter || "",
      linkedin: p.linkedin || "", threads: p.threads || "",
      snapchat: p.snapchat || "",
      portfolio_url: p.portfolio_url || "",
      showreel_url: p.showreel_url || "",
    });
  }, [user]);

  const skillsForCategory = useMemo(() => {
    const list = SKILLS_BY_CATEGORY[f.category];
    return list ? [...list, ...GENERIC_SKILLS] : GENERIC_SKILLS;
  }, [f.category]);

  // Auto-detect: if user pastes a URL that clearly belongs to another platform, move it.
  const onSocialChange = (key, value) => {
    const detected = detectSocialUrl(value);
    if (detected && detected !== key) {
      toast.success(`Detected as ${detected} — moved to that field`);
      setF((prev) => ({ ...prev, [detected]: value, [key]: "" }));
      return;
    }
    setF((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f };
      // Only send non-empty strings and non-empty arrays
      Object.keys(payload).forEach(k => {
        const v = payload[k];
        if (v === "" || v === null || v === undefined) delete payload[k];
        if (Array.isArray(v) && v.length === 0) delete payload[k];
      });
      if (payload.height_cm) payload.height_cm = Number(payload.height_cm);
      if (payload.weight_kg) payload.weight_kg = Number(payload.weight_kg);
      await api.put("/users/me", payload);
      await refresh();
      toast.success("Profile updated");
      nav(`/u/${user.username}`);
    } catch (err) {
      toast.error(errMsg(err, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div data-testid="edit-profile-page" className="min-h-screen pb-24">
      <TopBar subtitle="Edit profile"/>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <BackButton fallback="/app/me" label="Back"/>

        <Section title="Craft">
          <F label="Talent category">
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="ep-category" className="bg-zinc-900 border-zinc-700 h-11"><SelectValue placeholder="Category"/></SelectTrigger>
              <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
                {CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Bio">
            <Textarea data-testid="ep-bio" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} placeholder="A few lines about your craft" className="bg-zinc-900 border-zinc-700 min-h-[80px]"/>
          </F>
        </Section>

        <Section title="Basics">
          <div className="grid grid-cols-2 gap-3">
            <F label="City"><Input data-testid="ep-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="bg-zinc-900 border-zinc-700"/></F>
            <F label="State"><Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className="bg-zinc-900 border-zinc-700"/></F>
            <F label="Height (cm)"><Input type="number" value={f.height_cm} onChange={(e) => setF({ ...f, height_cm: e.target.value })} className="bg-zinc-900 border-zinc-700"/></F>
            <F label="Weight (kg)"><Input type="number" value={f.weight_kg} onChange={(e) => setF({ ...f, weight_kg: e.target.value })} className="bg-zinc-900 border-zinc-700"/></F>
            <F label="Gender">
              <Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-700 h-10"><SelectValue placeholder="Select"/></SelectTrigger>
                <SelectContent className="bg-[#180D0D] border-zinc-800 text-white">
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Phone (WhatsApp)"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+91 98xxxxxxxx" className="bg-zinc-900 border-zinc-700"/></F>
          </div>
        </Section>

        <Section title="Languages" hint="Tick every language you can perform in">
          <ChipGroup
            options={LANGUAGES}
            selected={f.languages}
            onChange={(v) => setF({ ...f, languages: v })}
            testid="ep-langs"
          />
          <AddCustom placeholder="Add another language" onAdd={(v) => setF((p) => ({ ...p, languages: [...new Set([...p.languages, v])] }))}/>
        </Section>

        <Section title="Skills" hint="Handpicked options for your craft">
          <ChipGroup
            options={skillsForCategory}
            selected={f.skills}
            onChange={(v) => setF({ ...f, skills: v })}
            testid="ep-skills"
          />
          <AddCustom placeholder="Add a custom skill" onAdd={(v) => setF((p) => ({ ...p, skills: [...new Set([...p.skills, v])] }))}/>
        </Section>

        <Section title="Availability" hint="Casting directors see this at a glance">
          <ChipGroup
            options={AVAILABILITY_OPTIONS}
            selected={f.availability}
            onChange={(v) => setF({ ...f, availability: v })}
            testid="ep-availability"
          />
        </Section>

        <Section title="Experience & credits">
          <F label="Experience"><Textarea value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })} placeholder="Films, shows, gigs — anything you've done" className="bg-zinc-900 border-zinc-700 min-h-[60px]"/></F>
          <F label="Education"><Input value={f.education} onChange={(e) => setF({ ...f, education: e.target.value })} className="bg-zinc-900 border-zinc-700"/></F>
          <F label="Awards & recognition"><Textarea value={f.awards} onChange={(e) => setF({ ...f, awards: e.target.value })} className="bg-zinc-900 border-zinc-700 min-h-[60px]"/></F>
        </Section>

        <PortfolioSection user={user} refresh={refresh}/>

        {/* Iter 36: typed gallery uploader (Headshot / Traditional / Character Look / etc.) */}
        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
          <p className="font-display text-base font-semibold text-white mb-1">Portfolio Gallery (typed)</p>
          <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">
            Every image needs a Photo Type so casting directors can filter — Headshot, Traditional, Character Look, etc.
          </p>
          <GalleryUploader initialGallery={(user?.profile?.gallery_photos) || []}/>
        </div>

        <IntroVideoSection user={user} refresh={refresh}/>
        <ResumeSection user={user} refresh={refresh}/>
        <PortfolioPdfsSection user={user} refresh={refresh}/>
        <TrailerSection user={user} refresh={refresh}/>

        <Section title="Social & portfolio" hint="Paste any URL — we'll auto-detect the platform">
          <div className="space-y-2.5">
            {SOCIAL_PLATFORMS.map((s) => {
              const Icon = SOCIAL_ICONS[s.key] || Globe;
              return (
                <div key={s.key} className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-700 flex items-center justify-center text-zinc-200 shrink-0">
                    <Icon size={16}/>
                  </div>
                  <Input
                    data-testid={`social-${s.key}`}
                    value={f[s.key] || ""}
                    onChange={(e) => onSocialChange(s.key, e.target.value)}
                    placeholder={s.placeholder}
                    className="flex-1 bg-zinc-900 border-zinc-700"
                  />
                </div>
              );
            })}
          </div>
        </Section>

        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
          <Button data-testid="ep-save" onClick={save} disabled={busy} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
            {busy && <Loader2 className="animate-spin mr-2" size={16}/>} Save changes
          </Button>
        </div>

        <div id="security" className="scroll-mt-24">
          <ChangePasswordSection user={user}/>
        </div>

        <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }} className="w-full flex items-center justify-center gap-2 text-red-400 rounded-full border border-zinc-800 bg-zinc-900 h-11 hover:border-red-500/40 transition-colors">
          <LogOut size={14}/> Log out
        </button>
      </div>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
      <div className="mb-3">
        <p className="font-display text-base font-semibold text-white">{title}</p>
        {hint && <p className="text-xs text-zinc-400 mt-0.5">{hint}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-zinc-200 text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function KYCSection() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ aadhaar_last4: "", phone: "", otp: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/verification/kyc/status").then(r => setStatus(r.data.kyc));
  }, []);

  const sendOtp = async () => {
    try {
      const { data } = await api.post("/verification/otp/send");
      setOtpSent(true);
      toast.success(data.hint || "OTP sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "OTP failed"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/verification/kyc/submit", form);
      const { data } = await api.get("/verification/kyc/status");
      setStatus(data.kyc);
      toast.success("KYC submitted — admin will review");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "KYC failed");
    } finally { setBusy(false); }
  };

  if (status?.phone_otp_verified) {
    return (
      <div data-testid="kyc-done" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5">
        <p className="font-display text-lg font-semibold text-white flex items-center gap-2">
          <Check size={16} className="text-emerald-400"/> KYC on file
        </p>
        <p className="text-xs text-zinc-300 mt-1">Aadhaar ending {status.aadhaar_last4} · Phone {status.phone}</p>
        <p className="text-xs text-zinc-400 mt-1">{status.aadhaar_verified ? "Aadhaar verified by admin ✓" : "Aadhaar pending admin verification…"}</p>
      </div>
    );
  }

  return (
    <div data-testid="kyc-section" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
      <p className="font-display text-lg font-semibold mb-1 text-white">KYC (identity check)</p>
      <p className="text-xs text-zinc-400 mb-4">Required before your Verified badge goes live. Nothing shared publicly.</p>
      <div className="space-y-3">
        <F label="Last 4 digits of your Aadhaar">
          <Input data-testid="kyc-aadhaar" maxLength={4} inputMode="numeric" value={form.aadhaar_last4} onChange={(e) => setForm({ ...form, aadhaar_last4: e.target.value.replace(/\D/g,'') })} placeholder="1234" className="bg-zinc-900 border-zinc-700"/>
        </F>
        <F label="Mobile number">
          <div className="flex gap-2">
            <Input data-testid="kyc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98xxxxxxxx" className="flex-1 bg-zinc-900 border-zinc-700"/>
            <Button data-testid="kyc-send-otp" type="button" onClick={sendOtp} disabled={!form.phone} className="rounded-full h-10 px-4 bg-zinc-900 border border-zinc-700 hover:border-[#DC2626]/50 text-sm">Send OTP</Button>
          </div>
        </F>
        {otpSent && (
          <F label="OTP (use 123456 in demo)">
            <Input data-testid="kyc-otp" maxLength={6} inputMode="numeric" value={form.otp} onChange={(e) => setForm({ ...form, otp: e.target.value.replace(/\D/g,'') })} placeholder="123456" className="bg-zinc-900 border-zinc-700"/>
          </F>
        )}
        <Button data-testid="kyc-submit" onClick={submit} disabled={busy || !form.aadhaar_last4 || !form.phone || !form.otp} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          {busy ? <><Loader2 className="animate-spin mr-2" size={14}/> Submitting…</> : "Submit KYC"}
        </Button>
        <p className="text-[11px] text-zinc-400">OTP delivery is <span className="text-[#DC2626]">MOCKED</span> for demo. Use <span className="text-white">123456</span> to proceed.</p>
      </div>
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function PortfolioSection({ user, refresh }) {
  const photos = user?.profile?.photos || [];
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Photo added");
    } catch (err) {
      toast.error(errMsg(err, "Photo upload failed"));
    } finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async (path) => {
    try {
      await api.delete(`/users/me/photo`, { params: { path } });
      await refresh();
      toast.success("Photo removed");
    } catch {}
  };
  return (
    <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
      <div className="mb-3">
        <p className="font-display text-base font-semibold text-white">Portfolio photos</p>
        <p className="text-xs text-zinc-400 mt-0.5">Headshots, on-stage moments, behind-the-scenes (max 10)</p>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {photos.map((path) => (
          <div key={path} className="aspect-square rounded-lg overflow-hidden bg-black border border-zinc-800 relative group">
            <img src={`${BACKEND}/api/users/photo/${path}`} alt="portfolio" className="w-full h-full object-cover"/>
            <button data-testid={`photo-del-${path.slice(-10)}`} onClick={() => remove(path)} className="absolute top-1 right-1 bg-black/70 text-red-400 rounded-full w-6 h-6 flex items-center justify-center text-xs">×</button>
          </div>
        ))}
        {photos.length < 10 && (
          <label data-testid="photo-add" className="aspect-square rounded-lg border-2 border-dashed border-zinc-700 hover:border-[#DC2626]/50 flex items-center justify-center text-zinc-400 cursor-pointer transition-colors">
            {busy ? <Loader2 className="animate-spin" size={16}/> : <Plus size={20}/>}
            <input type="file" accept="image/*" className="hidden" onChange={upload}/>
          </label>
        )}
      </div>
    </div>
  );
}

function IntroVideoSection({ user, refresh }) {
  const [videos, setVideos] = useState([]);
  const intro = user?.profile?.intro_video_id;
  useEffect(() => { api.get("/videos/my/list").then(r => setVideos(r.data.videos || [])); }, []);
  const pick = async (id) => {
    try {
      await api.post(`/users/me/intro-video/${id}`);
      await refresh();
      toast.success("Set as introduction video");
    } catch (e) { toast.error(errMsg(e, "Failed")); }
  };
  const clear = async () => {
    try { await api.delete("/users/me/intro-video"); await refresh(); toast.success("Cleared"); } catch {}
  };
  return (
    <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
      <div className="mb-3">
        <p className="font-display text-base font-semibold text-white">Introduction video</p>
        <p className="text-xs text-zinc-400 mt-0.5">The first video people see on your profile</p>
      </div>
      {videos.length === 0 ? (
        <p className="text-sm text-zinc-400">Upload a performance first to pick your intro.</p>
      ) : (
        <div className="space-y-2">
          {videos.filter(v => v.kind !== "audio").map((v) => (
            <label key={v.id} data-testid={`intro-opt-${v.id}`} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 cursor-pointer hover:border-[#DC2626]/40">
              <input type="radio" name="intro" checked={intro === v.id} onChange={() => pick(v.id)} className="accent-[#DC2626]"/>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{v.title || "Untitled"}</p>
                <p className="text-xs text-zinc-400">{v.duration}s</p>
              </div>
            </label>
          ))}
          {intro && (
            <Button type="button" onClick={clear} className="rounded-full h-9 bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs hover:border-red-500/40">Clear selection</Button>
          )}
        </div>
      )}
    </div>
  );
}

function ResumeSection({ user, refresh }) {
  const has = user?.profile?.resume_url;
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please pick a PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Resume uploaded");
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
      <div className="mb-3">
        <p className="font-display text-base font-semibold text-white">Resume (PDF)</p>
        <p className="text-xs text-zinc-400 mt-0.5">Casting directors love a clean 1-pager</p>
      </div>
      <label data-testid="resume-upload" className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-zinc-700 hover:border-[#DC2626]/50 h-14 cursor-pointer text-sm text-zinc-200 transition-colors">
        {busy ? <Loader2 className="animate-spin" size={14}/> : <>{has ? "Replace PDF" : "Upload PDF"} </>}
        <input type="file" accept="application/pdf" className="hidden" onChange={upload}/>
      </label>
      {has && (
        <a href={`${BACKEND}/api/users/resume/${has}`} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-[#DC2626] hover:underline">View current resume →</a>
      )}
    </div>
  );
}

function PortfolioPdfsSection({ user, refresh }) {
  const docs = user?.profile?.portfolio_pdfs || [];
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please pick a PDF"); e.target.value = ""; return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB per PDF"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name.replace(/\.pdf$/i, ""));
      await api.post("/users/me/portfolio-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("PDF added to portfolio");
      setTitle("");
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    if (!confirm("Delete this PDF?")) return;
    try { await api.delete(`/users/me/portfolio-pdf/${id}`); await refresh(); toast.success("Deleted"); }
    catch (err) { toast.error(errMsg(err, "Delete failed")); }
  };

  return (
    <div data-testid="portfolio-pdfs-section" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5">
      <div className="mb-3">
        <p className="font-display text-base font-semibold text-white flex items-center gap-2"><FileText size={16} className="text-[#DC2626]"/> Portfolio documents</p>
        <p className="text-xs text-zinc-400 mt-0.5">Decks, certificates, credits sheet — max 5 PDFs, 8MB each</p>
      </div>
      {docs.length > 0 && (
        <div className="space-y-2 mb-3">
          {docs.map((d) => (
            <div key={d.id} data-testid={`pdf-row-${d.id}`} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
              <FileText size={16} className="text-[#DC2626] shrink-0"/>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{d.title || "Document"}</p>
                <p className="text-[11px] text-zinc-400">{Math.round((d.size || 0)/1024)} KB · {new Date(d.uploaded_at).toLocaleDateString()}</p>
              </div>
              <a href={`${BACKEND}/api/users/portfolio-pdf/${d.path}`} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-white shrink-0" data-testid={`pdf-view-${d.id}`}><Download size={14}/></a>
              <button data-testid={`pdf-del-${d.id}`} onClick={() => remove(d.id)} className="text-red-400 hover:text-red-300 shrink-0"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      )}
      {docs.length < 5 && (
        <div className="space-y-2">
          <Input data-testid="pdf-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Document title (optional — e.g. 'Acting reel deck')" className="bg-zinc-900 border-zinc-700 h-9 text-sm"/>
          <label data-testid="pdf-upload" className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-zinc-700 hover:border-[#DC2626]/50 h-14 cursor-pointer text-sm text-zinc-200 transition-colors">
            {busy ? <Loader2 className="animate-spin" size={14}/> : <><Plus size={14}/> Upload PDF ({docs.length}/5)</>}
            <input type="file" accept="application/pdf" className="hidden" onChange={upload}/>
          </label>
        </div>
      )}
    </div>
  );
}

function TrailerSection({ user, refresh }) {
  const trailer = user?.profile?.trailer;
  const [busy, setBusy] = useState(false);
  const uname = user?.username;
  const trailerUrl = trailer?.path ? `${BACKEND}/api/users/trailer/${trailer.path}` : "";
  const shareText = `Watch my NextScene trailer 🎬 ${window.location.origin}/${uname}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  const generate = async () => {
    if (!confirm("Generate a fresh trailer from your top 3 performances? Takes ~30-60 seconds.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/users/me/trailer/generate", {}, { timeout: 180000 });
      toast.success(`Trailer ready (${data.trailer.duration}s)`);
      await refresh();
    } catch (err) { toast.error(errMsg(err, "Trailer generation failed")); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="trailer-section" className="rounded-2xl border border-zinc-800 bg-gradient-to-br from-[#180D0D] to-[#0A0605] p-5">
      <div className="mb-3">
        <p className="font-display text-base font-semibold text-white flex items-center gap-2">
          <Film size={16} className="text-[#DC2626]"/> Bollywood trailer reel
          <span className="ml-auto text-[10px] rounded-full bg-[#F5C462]/15 text-[#F5C462] px-2 py-0.5 border border-[#F5C462]/30">Auto</span>
        </p>
        <p className="text-xs text-zinc-400 mt-0.5">Auto-stitch your top 3 performances into a 30-second vertical trailer with fades and @username watermark — perfect for WhatsApp.</p>
      </div>

      {trailer?.path ? (
        <div className="space-y-3">
          <video data-testid="ep-trailer-video" src={trailerUrl} controls playsInline className="w-full rounded-xl bg-black max-h-[70vh] object-contain aspect-[9/16] mx-auto"/>
          <p className="text-[11px] text-zinc-500">Generated {new Date(trailer.generated_at).toLocaleString()} · {Math.round((trailer.size || 0)/1024)} KB</p>
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="trailer-regenerate" type="button" onClick={generate} disabled={busy} className="h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-sm">
              {busy ? <><Loader2 className="animate-spin mr-2" size={14}/> Regenerating…</> : <>Regenerate</>}
            </Button>
            <a data-testid="trailer-whatsapp" href={whatsappUrl} target="_blank" rel="noreferrer" className="h-11 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-black font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors">
              <Send size={14}/> Share on WhatsApp
            </a>
          </div>
        </div>
      ) : (
        <Button data-testid="trailer-generate" type="button" onClick={generate} disabled={busy} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          {busy ? <><Loader2 className="animate-spin mr-2" size={14}/> Building your trailer (30-60s)…</> : <><Film size={14} className="mr-2"/> Generate trailer</>}
        </Button>
      )}
      <p className="text-[10px] text-zinc-500 mt-2">Requires at least 2 uploaded performances (video, non-audio).</p>
    </div>
  );
}


function ChangePasswordSection({ user }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [verifyToken, setVerifyToken] = useState("");
  const isGoogle = user?.auth_provider !== "email";

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.new_password !== form.confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(errMsg(err, "Change failed")); }
    finally { setBusy(false); }
  };

  const sendVerification = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post("/auth/send-verification");
      if (data.already_verified) { toast.success("Email already verified"); return; }
      toast.success(data.verification_hint || "Verification link sent");
    } catch (err) { toast.error(errMsg(err, "Could not send")); }
    finally { setEmailBusy(false); }
  };
  const verifyNow = async (e) => {
    e.preventDefault();
    if (!verifyToken) return;
    try {
      await api.post("/auth/verify-email", { token: verifyToken });
      toast.success("Email verified");
      setVerifyToken("");
      window.location.reload();
    } catch (err) { toast.error(errMsg(err, "Invalid token")); }
  };

  return (
    <div data-testid="account-security" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5 space-y-5">
      <div>
        <p className="font-display text-base font-semibold text-white flex items-center gap-2"><Lock size={14} className="text-[#DC2626]"/> Account security</p>
        <p className="text-xs text-zinc-400 mt-0.5">Keep your account safe</p>
      </div>

      {!user?.email_verified && (
        <div data-testid="verify-email-block" className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <p className="text-sm text-amber-300 font-semibold flex items-center gap-2"><Mail size={14}/> Verify your email</p>
          <p className="text-xs text-zinc-300 mt-1 leading-relaxed">Referrals only count once you verify. Uploads still work.</p>
          <Button data-testid="send-verify-btn" type="button" onClick={sendVerification} disabled={emailBusy} className="mt-3 h-9 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-xs">
            {emailBusy ? <Loader2 className="animate-spin" size={12}/> : "Send verification link"}
          </Button>
          <form onSubmit={verifyNow} className="mt-3 flex gap-2">
            <Input data-testid="verify-token-input" value={verifyToken} onChange={(e) => setVerifyToken(e.target.value)} placeholder="Paste verification token" className="flex-1 bg-zinc-900 border-zinc-800 h-9 text-xs font-mono"/>
            <Button data-testid="verify-token-submit" type="submit" className="h-9 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-xs">Verify</Button>
          </form>
        </div>
      )}
      {user?.email_verified && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-emerald-300 text-xs flex items-center gap-2">
          <ShieldCheck size={14}/> Email verified
        </div>
      )}

      {isGoogle ? (
        <p className="text-xs text-zinc-400">You signed in with Google — no password to change.</p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-zinc-300 text-xs">Current password</Label>
            <Input data-testid="cp-current" type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800"/>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">New password (min 6)</Label>
            <Input data-testid="cp-new" type="password" minLength={6} required value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800"/>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Confirm new password</Label>
            <Input data-testid="cp-confirm" type="password" minLength={6} required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1 bg-zinc-900 border-zinc-800"/>
          </div>
          <Button data-testid="cp-submit" type="submit" disabled={busy} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
            {busy ? <Loader2 className="animate-spin" size={14}/> : "Update password"}
          </Button>
        </form>
      )}
    </div>
  );
}